import { 
	createSlice,
	createSelector,
	createAsyncThunk,
	createEntityAdapter
} from '@reduxjs/toolkit'

import {
	envAwarePost,
	dateToAPIStandard
} from '../../util/util'

import {pathsIdentical} from './util'

export const getMenu = createAsyncThunk(
	'customizationDashboard/getMenu',
	async payload => await envAwarePost(
		'api/settings/',
		{action:'getMenu'}
	)
)

export const resetMenu = createAsyncThunk(
	'customizationDashboard/resetMenu',
	async payload => await envAwarePost(
		'api/settings/',
		{action:'resetMenu'}
	)
)

export const getEditableClientMenu = createAsyncThunk(
	'customizationDashboard/getEditableClientMenu',
	async payload => await envAwarePost(
		'api/settings/',
		{action:'getEditableClientMenu'}
	)
)

export const createMenuGroup = createAsyncThunk(
	'customizationDashboard/createMenuGroup',
	async payload => await envAwarePost(
		'api/settings/',
		{action:'createMenuGroup',...payload}
	)
)

export const editMenuGroup = createAsyncThunk(
	'customizationDashboard/editMenuGroup',
	async payload => await envAwarePost(
		'api/settings/',
		{action:'editMenuGroup',...payload}
	)
)

export const removeMenuGroup = createAsyncThunk(
	'customizationDashboard/removeMenuGroup',
	async payload => await envAwarePost(
		'api/settings/',
		{action:'removeMenuGroup',...payload}
	)
)



/*------------------------------------

Algorithm spec:
- set correct path on tgt item
- insert item into correct location
- scan entire object and remove the old copy of the src item

------------------------------------*/
export const arrangeMenu = createAsyncThunk(
	'customizationDashboard/arrangeMenu',
	async ({srcPath,tgtPath},{dispatch,getState})=> {
		//destructure enabled/disabled
		const {
			customizationDashboard:{
				clientMenu:{
					enabled,
					disabled
				}
			}
		} = getState()

		//paths assume that the enabled and disabled
		//items are stored in an array where the enabled
		//items are first then the disabled
		const menuArray = [enabled,disabled]
		//JSON hack for deep copy (google it)
		let update = JSON.parse(JSON.stringify(menuArray))

		//get source item
		//JSON hack for deep copy (google it)
		const srcItem = JSON.parse(JSON.stringify(
			srcPath.length === 3
			? menuArray[srcPath[0]][srcPath[1]].nav_items[srcPath[2]]
			: menuArray[srcPath[0]][srcPath[1]]
		))

		//update it to have the correct path
		srcItem.path = tgtPath
		//correctly mark src item as enabled/disbled
		//if tgtPath[0] == 1 then enabled = 0
		//if tgtPath[0] == 0 then enabled = 1
		//Thus we can use tgtPath[0]^1 (XOR) to correctly set enabled
		srcItem.enabled = tgtPath[0]^1
		
		//insert srcItem into target location
		const targetArray = (
			tgtPath.length === 3
			? update[tgtPath[0]][tgtPath[1]].nav_items
			: update[tgtPath[0]]
		)
		const tgtIdx = tgtPath[tgtPath.length-1]
		const targetArrayUpdate = [
			...targetArray.slice(0, tgtIdx),
			srcItem,
			...targetArray.slice(tgtIdx)
		]
		//replace target array on update with the updated version
		if(tgtPath.length === 3){
			update[tgtPath[0]][tgtPath[1]].nav_items = targetArrayUpdate
		}else {
			update[tgtPath[0]] = targetArrayUpdate
		}
		//loop map update and remove the original copy of the source item
		update = update.map((rootItems,i)=>{
			//if the source object was not in the root items
			//(ie in enabled/disabled) then return the array
			//since there is nothing to remove
			if ( i !== srcPath[0] ) return rootItems
			//if item is not nested in a group then filter from the
			//root items.
			if ( srcPath.length === 2 ) return rootItems.filter((navItem,j)=>{
				//don't remove if the item ids are different
				//or if the item is the new copy of the src item
				//in the tgt location
				return navItem.id !== srcItem.id || pathsIdentical([i,j],tgtPath)
			})
			//item is nested in a group so map over the root items 
			//find the group and filter it out of the group
			return rootItems.map((navItem,j)=>{
				//if not a group return the item. since we are looking
				//for a group
				if(navItem.item_type!=='menu_group') return navItem
				//if it is a group filter out the srcItem if it's found
				return {
					...navItem,
					nav_items:navItem.nav_items.filter((subItem,k)=>{
						//don't remove if the item ids are different
						//or if the item is the new copy of the src item
						//in the tgt location
						return subItem.id !== srcItem.id || pathsIdentical([i,j,k],tgtPath)
					})
				}
			})
		})
		//map update into form expected by API
		//disabled items at the end of the array
		const menu = [
			...update[0].map(item=>({
				...item,
				//mark this item enabled
				enabled:1,
				//mark sub items enabled
				nav_items:item.nav_items?.map(sub=>({
					...sub,
					enabled:1
				})),
			})),
			...update[1].map(item=>({
				...item,
				//mark this item disabled
				enabled:0,
				//mark sub items disabled
				nav_items:item.nav_items?.map(sub=>({
					...sub,
					enabled:0
				})),
			})),
		]

		dispatch(setMenu(menu))

		return await envAwarePost('api/settings/',{
			action:'arrangeMenu',
			menu
		})
	}
)

const parseMenuItems = items =>{
	const enabled = []
	const disabled = []
	items.forEach(item=>{
		if(Boolean(item.enabled)){
			enabled.push(item)
		}else{
			disabled.push(item)
		}
	})
	return {enabled,disabled}
}

const slice = createSlice({
	name: 'customizationDashboard',
	initialState : {
		menu:{
			status:'init',
			items:[]
		},
		clientMenu:{
			status:'init',
			enabled:[],
			disabled:[]
		},
	},
	reducers : {
		setMenu:(state,action)=>{
			const {enabled,disabled} = parseMenuItems(
				action.payload
			)
			state.clientMenu.enabled = enabled
			state.clientMenu.disabled = disabled
		}
	},
	extraReducers : {
		[getEditableClientMenu.pending] : (state)=>{
			state.clientMenu.status = 'pending'
		},
		[getEditableClientMenu.fulfilled] : (state,action)=>{
			state.clientMenu.status = 'idle'
			const {enabled,disabled} = parseMenuItems(
				action.payload.data.menu
			)
			state.clientMenu.enabled = enabled
			state.clientMenu.disabled = disabled
		},
		[getMenu.pending] : (state)=>{
			state.menu.status = 'pending'
		},
		[getMenu.fulfilled] : (state,action)=>{
			state.menu.status = 'idle'
			state.menu.items = action.payload.data.menu
		},
		[resetMenu.fulfilled] : (state,action)=>{
			state.clientMenu.status = 'idle'
			const {enabled,disabled} = parseMenuItems(
				action.payload.data.menu
			)
			state.clientMenu.enabled = enabled
			state.clientMenu.disabled = disabled
		},
		[createMenuGroup.fulfilled] : (state,action)=>{
			const {enabled,disabled} = parseMenuItems(
				action.payload.data.menu
			)
			state.clientMenu.enabled = enabled
			state.clientMenu.disabled = disabled
		},
		[editMenuGroup.fulfilled] : (state,action)=>{
			const {enabled,disabled} = parseMenuItems(
				action.payload.data.menu
			)
			state.clientMenu.enabled = enabled
			state.clientMenu.disabled = disabled
		},
		[removeMenuGroup.fulfilled] : (state,action)=>{
			const {enabled,disabled} = parseMenuItems(
				action.payload.data.menu
			)
			state.clientMenu.enabled = enabled
			state.clientMenu.disabled = disabled
		},
		[arrangeMenu.fulfilled] : (state,action)=>{
			const {enabled,disabled} = parseMenuItems(
				action.payload.data.menu
			)
			//state.clientMenu.enabled = enabled
			//state.clientMenu.disabled = disabled
		},
	}
})

export const {setMenu} = slice.actions


export default slice.reducer